import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Printer, Download, FileText, ArrowLeft, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsAndConditions = () => {
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language === 'es' ? 'es' : 'en');
  const contentRef = useRef(null);

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'es' : 'en');
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    window.print(); // Browser print dialog allows saving as PDF
  };

  const terms = {
    en: {
      title: "ONE2TEES USA TERMS AND CONDITIONS OF SERVICE",
      subtitle: "Online Store • E-Commerce Platform • In-Store Transactions",
      effectiveDate: "Effective Date: June 1, 2025",
      lastUpdated: "Last Updated: March 2026",
      governingLaw: "Governing Law: State of Florida • United States Federal Law",
      importantNotice: "IMPORTANT — READ BEFORE PLACING YOUR ORDER",
      importantText: "By placing an order — online, by phone, by email, or in person — you acknowledge that you have read, understood, and agreed to these Terms and Conditions in their entirety. These terms are legally binding. If you do not agree, do not place an order.",
      sections: [
        {
          title: "1. OFFICIAL LANGUAGE",
          content: "The official language of all communications, agreements, and transactions is English. It is the customer's responsibility to obtain professional translation services if needed. No exceptions or accommodations will be made for language barriers after an order has been placed."
        },
        {
          title: "2. PAYMENT — 100% REQUIRED IN ADVANCE",
          content: "⚠ FULL PAYMENT IS REQUIRED BEFORE PRODUCTION BEGINS — NO EXCEPTIONS.\n\nONE2TEES USA requires 100% payment in advance for all orders. Production will not begin, artwork will not be reviewed, and no materials will be ordered until full payment has been received and cleared.\n\nA payment plan may be considered ONLY if expressly negotiated and agreed upon in writing by an authorized representative of ONE2TEES USA prior to placing the order. Verbal agreements regarding payment terms are not valid.",
          subsections: [
            {
              title: "2.1 Accepted Payment Methods",
              content: "• Visa, Mastercard, and Discover (credit/debit cards)\n• ACH Bank Transfer\n• Zelle (business account only)\n• Venmo (business account only)\n• Cash (in-person transactions only)\n\n⚠ American Express is NOT accepted. A credit card processing surcharge of 3.5% will be applied to all card transactions, in compliance with Florida Statutes §501.0117. This fee will be disclosed at checkout."
            },
            {
              title: "2.2 Handling Fee",
              content: "A non-refundable handling fee of 3.5% is automatically applied to all orders to cover processing, packaging, and administrative costs. This fee is itemized on your invoice."
            }
          ]
        },
        {
          title: "3. ARTWORK AND FILE SUBMISSION REQUIREMENTS",
          subsections: [
            {
              title: "3.1 File Specifications",
              content: "All artwork must be submitted in one of the following formats: PDF (preferred), JPG, EPS, TIFF, or PNG, meeting these specifications:\n\n• Minimum resolution: 300 DPI at final print size\n• Color mode: CMYK — NOT RGB\n• Bleed: 0.125\" on each side (1\" for bumper stickers)\n• Fonts must be embedded or converted to outlines\n• No visible design lines, guides, templates, or watermarks"
            },
            {
              title: "3.2 Customer Responsibility for File Quality",
              content: "⚠ IF THE CUSTOMER APPROVES AN ARTWORK FILE, THEY ASSUME FULL RESPONSIBILITY FOR ITS QUALITY.\n\nThe Company will print exactly what is submitted and approved. If a customer approves a low-resolution, incorrectly colored, or otherwise deficient file, the Company will proceed with production and NO REFUND OR REPRINT will be issued.\n\nThe Company is not responsible for:\n• Typos, spelling errors, or design mistakes in submitted files\n• Low-resolution images that print blurry or pixelated\n• Incorrect colors from RGB-to-CMYK conversion\n• Missing fonts or unembedded images\n• Design elements outside safe zones or in bleed areas\n• Any difference between on-screen appearance and final printed product"
            }
          ]
        },
        {
          title: "4. ARTWORK APPROVAL AND CHANGE WINDOW",
          content: "⚠ ONCE ARTWORK IS APPROVED, CHANGES MUST BE REQUESTED WITHIN 1 (ONE) HOUR.\n\nAfter the customer approves artwork — whether by written confirmation, email, digital signature, or click-through approval — the customer has a maximum of ONE (1) HOUR to request any changes.\n\nAfter this window expires, production may begin at any time and changes will not be accepted. Change requests after the 1-hour window may be accommodated at the Company's sole discretion and may result in additional charges and/or extended production timelines.\n\nThe Company is not obligated to accommodate late change requests. It is the Customer's sole responsibility to carefully review all artwork proofs for accuracy — including spelling, colors, sizes, placement, and design — BEFORE approving."
        },
        {
          title: "5. NO REFUND POLICY — CUSTOM PRODUCTS",
          content: "⚠ ALL SALES OF CUSTOM PRODUCTS ARE FINAL. NO REFUNDS. NO EXCHANGES. NO EXCEPTIONS.\n\nOnce an order is placed and payment is received, ALL SALES ARE FINAL. Once artwork has been approved by the customer, there are absolutely NO REFUNDS, RETURNS, or EXCHANGES under any circumstances, including but not limited to:\n\n• Change of mind or buyer's remorse\n• Errors in customer-submitted or customer-approved artwork\n• Incorrect sizes or quantities selected by the customer\n• Color variations within acceptable industry standards\n• Delays in delivery caused by shipping carriers\n• Products that were not reviewed before approval\n\nThis policy is standard in the custom printing industry and is enforceable under Florida and federal law for custom-manufactured goods.",
          subsections: [
            {
              title: "5.1 Limited Exception — Company Defect Only",
              content: "A reprint or refund will ONLY be considered if:\n• The Company produced the product with a significant manufacturing defect caused solely by our error\n• The Company printed a product materially different from the approved artwork (not color variation)\n• The product was damaged during our internal production process\n\nTo request a remedy under this exception, the Customer must:\n• Contact ONE2TEES USA within 5 business days of receiving the order\n• Provide clear photographic evidence of the defect\n• Retain all defective products for potential return\n\nApproved remedies are limited to reprint or store credit — not cash refunds — unless otherwise required by applicable law."
            },
            {
              title: "5.2 Pre-Ordered and Custom-Specified Garments",
              content: "No returns or exchanges will be accepted on pre-ordered garments, or items ordered with custom specifications requested by the customer, under any circumstances."
            }
          ]
        },
        {
          title: "6. SHIPPING AND DELIVERY POLICY",
          subsections: [
            {
              title: "6.1 Shipping Costs",
              content: "Shipping is calculated at 20% of the order subtotal for standard shipping. Free shipping is available on orders exceeding $250.00 (before taxes and fees). Expedited shipping options are available at actual carrier rates."
            },
            {
              title: "6.2 Delivery Risk — Customer Responsibility",
              content: "⚠ ONCE THE PACKAGE IS TRANSFERRED TO THE CARRIER, DELIVERY IS THE CUSTOMER'S RESPONSIBILITY.\n\nTitle and risk of loss pass to the Customer upon delivery of the product to the shipping carrier. ONE2TEES USA is NOT responsible for:\n• Packages lost, stolen, or damaged in transit\n• Delays caused by the carrier, weather, or other circumstances beyond our control\n• Incorrect delivery addresses provided by the customer\n• Packages marked as delivered but not received by the customer"
            },
            {
              title: "6.3 Shipping Insurance",
              content: "Shipping insurance is available upon request at an additional cost. If the Customer wishes to insure their shipment, they MUST request shipping insurance IN WRITING before the order is shipped."
            },
            {
              title: "6.4 Estimated Delivery Times",
              content: "• Florida: 3–7 business days after production completion\n• Continental U.S.: 5–10 business days after production completion\n• All times are estimates only and not guaranteed"
            }
          ]
        },
        {
          title: "7. PRODUCTION TIMELINE",
          content: "Standard production time is 2 to 10 business days from the date of order confirmation and receipt of approved artwork. Production timelines are estimates, not guarantees.",
          subsections: [
            {
              title: "7.1 Rush Orders",
              content: "Rush production (same-day or next-day) is available subject to current capacity and at the Company's sole discretion. Rush orders incur an additional fee of 25% of the order total plus any expedited shipping costs. Rush availability must be confirmed in writing before relying upon it."
            }
          ]
        },
        {
          title: "8. ORDER CANCELLATION AND MODIFICATIONS",
          content: "Orders may not be cancelled or modified once production has commenced. Cancellation requests received after production has started may be subject to a fee equal to all costs incurred to that point, including materials and labor. Once the Customer has approved artwork, cancellation is not available."
        },
        {
          title: "9. SALES TAX",
          content: "Florida state sales tax (currently 7%) is mandatory and non-negotiable on all taxable sales to Florida customers, pursuant to Florida Statutes Chapter 212. Tax-exempt certificates must be provided BEFORE invoice issuance — exemptions cannot be applied retroactively."
        },
        {
          title: "10. EMBROIDERY SERVICES",
          content: "A one-time digitizing fee of $45.00 applies per unique embroidery design. If the Customer wishes to retain ownership of the digitized file, an additional file ownership fee of $45.00 is required."
        },
        {
          title: "11. INTELLECTUAL PROPERTY",
          content: "The Customer guarantees they own or have all necessary rights and permissions for any artwork, logos, images, or copyrighted material submitted. The Customer agrees to indemnify ONE2TEES USA from all claims arising from intellectual property infringement."
        },
        {
          title: "12. QUANTITY VARIANCE",
          content: "Custom printing is subject to a standard industry variance of -5% to +10% of the ordered quantity. Orders within this range are considered complete and will be billed accordingly."
        },
        {
          title: "13. LIMITATION OF LIABILITY",
          content: "TO THE MAXIMUM EXTENT PERMITTED BY LAW, ONE2TEES USA SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. The Company's total liability shall not exceed the amount paid for the specific order giving rise to the claim."
        },
        {
          title: "14. PRIVACY AND DATA PROTECTION",
          content: "ONE2TEES USA complies with the Florida Information Protection Act (FIPA), Fla. Stat. §501.171. We use SSL encryption for all online transactions. Customer artwork files are stored securely and used solely for order fulfillment. We do not sell personal information to third parties."
        },
        {
          title: "15. ONLINE TRANSACTIONS — ELECTRONIC COMMERCE",
          content: "Our online store is governed by the E-SIGN Act (15 U.S.C. §7001) and UETA (Fla. Stat. §668.50). Click-through acceptance constitutes a valid, binding agreement. You consent to receive electronic communications from ONE2TEES USA."
        },
        {
          title: "16. FORCE MAJEURE",
          content: "The Company shall not be liable for delays caused by circumstances beyond its reasonable control, including natural disasters, pandemics, government actions, supply chain disruptions, power outages, or acts of God."
        },
        {
          title: "17. DISPUTE RESOLUTION",
          content: "Parties agree to attempt informal resolution within 30 days of written notice. If unsuccessful, disputes shall be submitted to binding arbitration under AAA Commercial Rules in Martin County, Florida.\n\nCLASS ACTION WAIVER: Neither party may bring class action claims. These Terms are governed by Florida law."
        },
        {
          title: "18. STATUTE OF LIMITATIONS",
          content: "⚠ ANY CLAIM MUST BE FILED WITHIN ONE (1) YEAR OF THE CAUSE OF ACTION OR IT IS PERMANENTLY BARRED."
        },
        {
          title: "19. CONSUMER PROTECTION COMPLIANCE",
          content: "ONE2TEES USA complies with: Florida DUTPA (Fla. Stat. §501.201), FTC Act (15 U.S.C. §41), CAN-SPAM Act, ADA website accessibility standards, and all other applicable federal and state consumer protection laws."
        },
        {
          title: "20. INDEMNIFICATION",
          content: "The Customer agrees to indemnify, defend, and hold harmless ONE2TEES USA, its owners, officers, employees, and agents from all claims, damages, costs, and attorney's fees arising from: the Customer's use of services, breach of these Terms, intellectual property infringement, or false information provided."
        },
        {
          title: "21. MODIFICATIONS TO TERMS",
          content: "ONE2TEES USA reserves the right to modify these Terms at any time. Changes are effective upon posting with an updated date. Continued use of services constitutes acceptance of modified Terms."
        },
        {
          title: "22. SEVERABILITY AND ENTIRE AGREEMENT",
          content: "If any provision is found unenforceable, it shall be modified or severed without affecting remaining provisions. These Terms, together with our Privacy Policy and order confirmation, constitute the entire agreement between the parties."
        },
        {
          title: "23. CONTACT INFORMATION",
          content: "ONE2TEES USA\nPalm City, Florida\n\nPhone: (772) 209-1041\nWhatsApp: +1 (728) 209-1041\nEmail: sales@one2teesusa.com\nWebsite: www.one2teesusa.com\n\nHours: Monday–Friday, 8:00 AM – 5:00 PM ET"
        }
      ],
      quickReference: {
        title: "QUICK REFERENCE — KEY POLICIES",
        items: [
          "100% Payment: Required in full before any production begins.",
          "No Refunds: All custom product sales are final once artwork is approved.",
          "1-Hour Change Window: Changes must be requested within 1 hour of artwork approval.",
          "File Quality: Customer approves artwork = customer is responsible for quality.",
          "Shipping: Risk passes to customer when package reaches carrier. Insurance available on request.",
          "Errors: Company defects only qualify for reprint consideration within 5 business days.",
          "Rush Fee: 25% surcharge + expedited shipping. Must be confirmed in writing.",
          "Embroidery: $45 digitizing fee per design. File ownership requires additional $45.",
          "Tax: 7% Florida sales tax mandatory. Non-negotiable.",
          "Disputes: Binding arbitration in Martin County, FL. 1-year limitation."
        ]
      },
      backToHome: "Back to Home",
      print: "Print",
      downloadPDF: "Download PDF"
    },
    es: {
      title: "TÉRMINOS Y CONDICIONES DE SERVICIO DE ONE2TEES USA",
      subtitle: "Tienda en Línea • Plataforma de Comercio Electrónico • Transacciones en Tienda",
      effectiveDate: "Fecha de Vigencia: 1 de Junio de 2025",
      lastUpdated: "Última Actualización: Marzo 2026",
      governingLaw: "Ley Aplicable: Estado de Florida • Ley Federal de Estados Unidos",
      importantNotice: "IMPORTANTE — LEA ANTES DE REALIZAR SU PEDIDO",
      importantText: "Al realizar un pedido — en línea, por teléfono, por correo electrónico o en persona — usted reconoce que ha leído, entendido y aceptado estos Términos y Condiciones en su totalidad. Estos términos son legalmente vinculantes. Si no está de acuerdo, no realice un pedido.",
      sections: [
        {
          title: "1. IDIOMA OFICIAL",
          content: "El idioma oficial de todas las comunicaciones, acuerdos y transacciones es el inglés. Es responsabilidad del cliente obtener servicios de traducción profesional si es necesario. No se harán excepciones o adaptaciones por barreras del idioma después de que se haya realizado un pedido."
        },
        {
          title: "2. PAGO — 100% REQUERIDO POR ADELANTADO",
          content: "⚠ SE REQUIERE EL PAGO COMPLETO ANTES DE COMENZAR LA PRODUCCIÓN — SIN EXCEPCIONES.\n\nONE2TEES USA requiere el pago del 100% por adelantado para todos los pedidos. La producción no comenzará, el arte no será revisado y no se ordenarán materiales hasta que el pago completo haya sido recibido y procesado.\n\nUn plan de pago puede ser considerado SOLO si se negocia expresamente y se acuerda por escrito por un representante autorizado de ONE2TEES USA antes de realizar el pedido.",
          subsections: [
            {
              title: "2.1 Métodos de Pago Aceptados",
              content: "• Visa, Mastercard y Discover (tarjetas de crédito/débito)\n• Transferencia Bancaria ACH\n• Zelle (solo cuenta comercial)\n• Venmo (solo cuenta comercial)\n• Efectivo (solo transacciones en persona)\n\n⚠ American Express NO es aceptada. Se aplicará un recargo del 3.5% por procesamiento de tarjeta de crédito a todas las transacciones con tarjeta."
            },
            {
              title: "2.2 Tarifa de Manejo",
              content: "Se aplica automáticamente una tarifa de manejo no reembolsable del 3.5% a todos los pedidos para cubrir costos de procesamiento, empaque y administración."
            }
          ]
        },
        {
          title: "3. REQUISITOS DE ENVÍO DE ARCHIVOS Y ARTE",
          subsections: [
            {
              title: "3.1 Especificaciones de Archivos",
              content: "Todo el arte debe enviarse en uno de los siguientes formatos: PDF (preferido), JPG, EPS, TIFF o PNG, cumpliendo estas especificaciones:\n\n• Resolución mínima: 300 DPI al tamaño de impresión final\n• Modo de color: CMYK — NO RGB\n• Sangrado: 0.125\" en cada lado (1\" para calcomanías de parachoques)\n• Las fuentes deben estar incrustadas o convertidas a contornos"
            },
            {
              title: "3.2 Responsabilidad del Cliente por la Calidad del Archivo",
              content: "⚠ SI EL CLIENTE APRUEBA UN ARCHIVO DE ARTE, ASUME TODA LA RESPONSABILIDAD POR SU CALIDAD.\n\nLa Compañía imprimirá exactamente lo que se envíe y apruebe. Si un cliente aprueba un archivo de baja resolución, con colores incorrectos o deficiente, la Compañía procederá con la producción y NO SE EMITIRÁ REEMBOLSO NI REIMPRESIÓN."
            }
          ]
        },
        {
          title: "4. APROBACIÓN DE ARTE Y VENTANA DE CAMBIOS",
          content: "⚠ UNA VEZ APROBADO EL ARTE, LOS CAMBIOS DEBEN SOLICITARSE DENTRO DE 1 (UNA) HORA.\n\nDespués de que el cliente apruebe el arte — ya sea por confirmación escrita, correo electrónico, firma digital o aprobación por clic — el cliente tiene un máximo de UNA (1) HORA para solicitar cualquier cambio.\n\nDespués de que expire esta ventana, la producción puede comenzar en cualquier momento y no se aceptarán cambios."
        },
        {
          title: "5. POLÍTICA DE NO REEMBOLSO — PRODUCTOS PERSONALIZADOS",
          content: "⚠ TODAS LAS VENTAS DE PRODUCTOS PERSONALIZADOS SON FINALES. SIN REEMBOLSOS. SIN CAMBIOS. SIN EXCEPCIONES.\n\nUna vez que se realiza un pedido y se recibe el pago, TODAS LAS VENTAS SON FINALES. Una vez que el arte ha sido aprobado por el cliente, no hay absolutamente NINGÚN REEMBOLSO, DEVOLUCIÓN o CAMBIO bajo ninguna circunstancia.",
          subsections: [
            {
              title: "5.1 Excepción Limitada — Solo Defectos de la Compañía",
              content: "Una reimpresión o reembolso SOLO se considerará si:\n• La Compañía produjo el producto con un defecto de fabricación significativo causado únicamente por nuestro error\n• La Compañía imprimió un producto materialmente diferente del arte aprobado\n• El producto fue dañado durante nuestro proceso de producción interno"
            },
            {
              title: "5.2 Prendas Pre-ordenadas y con Especificaciones Personalizadas",
              content: "No se aceptarán devoluciones ni cambios en prendas pre-ordenadas o artículos ordenados con especificaciones personalizadas solicitadas por el cliente, bajo ninguna circunstancia."
            }
          ]
        },
        {
          title: "6. POLÍTICA DE ENVÍO Y ENTREGA",
          subsections: [
            {
              title: "6.1 Costos de Envío",
              content: "El envío se calcula al 20% del subtotal del pedido para envío estándar. Envío gratis disponible en pedidos que excedan $250.00 (antes de impuestos y tarifas)."
            },
            {
              title: "6.2 Riesgo de Entrega — Responsabilidad del Cliente",
              content: "⚠ UNA VEZ QUE EL PAQUETE SE TRANSFIERE AL TRANSPORTISTA, LA ENTREGA ES RESPONSABILIDAD DEL CLIENTE.\n\nEl título y riesgo de pérdida pasan al Cliente al entregar el producto al transportista. ONE2TEES USA NO es responsable de paquetes perdidos, robados o dañados en tránsito."
            },
            {
              title: "6.3 Seguro de Envío",
              content: "El seguro de envío está disponible a solicitud con un costo adicional. Si el Cliente desea asegurar su envío, DEBE solicitar el seguro de envío POR ESCRITO antes de que se envíe el pedido."
            },
            {
              title: "6.4 Tiempos de Entrega Estimados",
              content: "• Florida: 3–7 días hábiles después de completar la producción\n• EE.UU. Continental: 5–10 días hábiles después de completar la producción\n• Todos los tiempos son solo estimaciones y no están garantizados"
            }
          ]
        },
        {
          title: "7. TIEMPO DE PRODUCCIÓN",
          content: "El tiempo de producción estándar es de 2 a 10 días hábiles desde la fecha de confirmación del pedido y recepción del arte aprobado.",
          subsections: [
            {
              title: "7.1 Pedidos Urgentes",
              content: "La producción urgente (mismo día o día siguiente) está disponible sujeta a la capacidad actual. Los pedidos urgentes incurren en una tarifa adicional del 25% del total del pedido más cualquier costo de envío acelerado."
            }
          ]
        },
        {
          title: "8. CANCELACIÓN Y MODIFICACIONES DE PEDIDOS",
          content: "Los pedidos no pueden cancelarse ni modificarse una vez que la producción ha comenzado. Una vez que el Cliente ha aprobado el arte, la cancelación no está disponible."
        },
        {
          title: "9. IMPUESTO SOBRE VENTAS",
          content: "El impuesto sobre ventas del estado de Florida (actualmente 7%) es obligatorio y no negociable en todas las ventas gravables a clientes de Florida."
        },
        {
          title: "10. SERVICIOS DE BORDADO",
          content: "Se aplica una tarifa única de digitalización de $45.00 por diseño único de bordado. Si el Cliente desea retener la propiedad del archivo digitalizado, se requiere una tarifa adicional de $45.00."
        },
        {
          title: "11. PROPIEDAD INTELECTUAL",
          content: "El Cliente garantiza que posee o tiene todos los derechos y permisos necesarios para cualquier arte, logotipos, imágenes o material con derechos de autor enviado."
        },
        {
          title: "12. VARIACIÓN DE CANTIDAD",
          content: "La impresión personalizada está sujeta a una variación estándar de la industria de -5% a +10% de la cantidad ordenada."
        },
        {
          title: "13. LIMITACIÓN DE RESPONSABILIDAD",
          content: "EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY, ONE2TEES USA NO SERÁ RESPONSABLE DE NINGÚN DAÑO INDIRECTO, INCIDENTAL, ESPECIAL, CONSECUENTE O PUNITIVO."
        },
        {
          title: "14. PRIVACIDAD Y PROTECCIÓN DE DATOS",
          content: "ONE2TEES USA cumple con la Ley de Protección de Información de Florida (FIPA). Usamos encriptación SSL para todas las transacciones en línea."
        },
        {
          title: "15. TRANSACCIONES EN LÍNEA — COMERCIO ELECTRÓNICO",
          content: "Nuestra tienda en línea se rige por la Ley E-SIGN y UETA. La aceptación por clic constituye un acuerdo válido y vinculante."
        },
        {
          title: "16. FUERZA MAYOR",
          content: "La Compañía no será responsable de retrasos causados por circunstancias fuera de su control razonable, incluyendo desastres naturales, pandemias, acciones gubernamentales."
        },
        {
          title: "17. RESOLUCIÓN DE DISPUTAS",
          content: "Las partes acuerdan intentar una resolución informal dentro de 30 días de notificación escrita. Si no tiene éxito, las disputas se someterán a arbitraje vinculante bajo las Reglas Comerciales de AAA en el Condado de Martin, Florida.\n\nRENUNCIA A ACCIONES COLECTIVAS: Ninguna de las partes puede presentar demandas colectivas."
        },
        {
          title: "18. ESTATUTO DE LIMITACIONES",
          content: "⚠ CUALQUIER RECLAMO DEBE PRESENTARSE DENTRO DE UN (1) AÑO DE LA CAUSA DE ACCIÓN O QUEDARÁ PERMANENTEMENTE PROHIBIDO."
        },
        {
          title: "19. CUMPLIMIENTO DE PROTECCIÓN AL CONSUMIDOR",
          content: "ONE2TEES USA cumple con: Florida DUTPA, FTC Act, CAN-SPAM Act, estándares de accesibilidad ADA para sitios web."
        },
        {
          title: "20. INDEMNIZACIÓN",
          content: "El Cliente acepta indemnizar, defender y eximir de responsabilidad a ONE2TEES USA de todos los reclamos, daños, costos y honorarios de abogados."
        },
        {
          title: "21. MODIFICACIONES A LOS TÉRMINOS",
          content: "ONE2TEES USA se reserva el derecho de modificar estos Términos en cualquier momento. Los cambios son efectivos al publicarse con una fecha actualizada."
        },
        {
          title: "22. DIVISIBILIDAD Y ACUERDO COMPLETO",
          content: "Si alguna disposición se encuentra inaplicable, se modificará o separará sin afectar las disposiciones restantes."
        },
        {
          title: "23. INFORMACIÓN DE CONTACTO",
          content: "ONE2TEES USA\nPalm City, Florida\n\nTeléfono: (772) 209-1041\nWhatsApp: +1 (728) 209-1041\nEmail: sales@one2teesusa.com\nSitio Web: www.one2teesusa.com\n\nHorario: Lunes–Viernes, 8:00 AM – 5:00 PM ET"
        }
      ],
      quickReference: {
        title: "REFERENCIA RÁPIDA — POLÍTICAS CLAVE",
        items: [
          "100% Pago: Requerido completo antes de comenzar cualquier producción.",
          "Sin Reembolsos: Todas las ventas de productos personalizados son finales una vez que se aprueba el arte.",
          "Ventana de 1 Hora: Los cambios deben solicitarse dentro de 1 hora de la aprobación del arte.",
          "Calidad del Archivo: Cliente aprueba arte = cliente es responsable de la calidad.",
          "Envío: El riesgo pasa al cliente cuando el paquete llega al transportista.",
          "Errores: Solo defectos de la compañía califican para consideración de reimpresión dentro de 5 días hábiles.",
          "Tarifa Urgente: 25% de recargo + envío acelerado.",
          "Bordado: $45 tarifa de digitalización por diseño.",
          "Impuesto: 7% impuesto sobre ventas de Florida obligatorio.",
          "Disputas: Arbitraje vinculante en el Condado de Martin, FL. Limitación de 1 año."
        ]
      },
      backToHome: "Volver al Inicio",
      print: "Imprimir",
      downloadPDF: "Descargar PDF"
    }
  };

  const t = terms[language];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50 print:hidden">
        <div className="max-w-5xl mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700">
            <ArrowLeft className="w-5 h-5" />
            {t.backToHome}
          </Link>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={toggleLanguage} className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              {language === 'en' ? 'Español' : 'English'}
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              {t.print}
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadPDF}>
              <Download className="w-4 h-4 mr-2" />
              {t.downloadPDF}
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8" ref={contentRef}>
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center">
            <FileText className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">{t.title}</h1>
          <p className="text-slate-600">{t.subtitle}</p>
          <div className="mt-4 flex flex-wrap justify-center gap-4 text-sm text-slate-500">
            <span>{t.effectiveDate}</span>
            <span className="font-semibold text-cyan-600">{t.lastUpdated}</span>
            <span>{t.governingLaw}</span>
          </div>
        </div>

        {/* Important Notice */}
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-red-800 mb-3 flex items-center gap-2">
            ❗ {t.importantNotice}
          </h2>
          <p className="text-red-700">{t.importantText}</p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {t.sections.map((section, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-bold text-slate-800 mb-4 pb-2 border-b border-cyan-200">
                {section.title}
              </h2>
              {section.content && (
                <div className="text-slate-700 whitespace-pre-line mb-4">{section.content}</div>
              )}
              {section.subsections && (
                <div className="space-y-4 ml-4">
                  {section.subsections.map((sub, subIdx) => (
                    <div key={subIdx} className="bg-slate-50 rounded-lg p-4">
                      <h3 className="font-semibold text-slate-800 mb-2">{sub.title}</h3>
                      <div className="text-slate-700 whitespace-pre-line text-sm">{sub.content}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Quick Reference */}
        <div className="mt-8 bg-cyan-50 border-2 border-cyan-300 rounded-lg p-6">
          <h2 className="text-xl font-bold text-cyan-800 mb-4">{t.quickReference.title}</h2>
          <ul className="space-y-2">
            {t.quickReference.items.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-cyan-900">
                <span className="text-cyan-600 font-bold">•</span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-slate-500 text-sm">
          <p>ONE2TEES USA — Making Your Vision a Reality</p>
          <p>one2teesusa.com • (772) 209-1041 • sales@one2teesusa.com</p>
        </div>
      </main>
    </div>
  );
};

export default TermsAndConditions;
